import { useState, useCallback } from "react";
import { useToast } from "./use-toast";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type {
  ListUsersResponse,
  InviteUserRequest,
  InviteUserResponse,
  UpdateUserRoleRequest,
  UpdateUserRoleResponse,
  UpdateUserCompanyRequest,
  UpdateUserCompanyResponse,
  RemoveUserRequest,
  RemoveUserResponse,
} from "@/types/edge-functions";

export interface PlatformUser {
  id: string;
  name: string;
  email: string;
  role: "DiscoRun" | "Manager" | "Viewer" | "PAR";
  status: "Active" | "Invited";
  created_at?: string;
  last_sign_in_at?: string | null;
  expires_at?: string; // For invitations
  company_id?: string | null;
}

export function useUsers() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [invitations, setInvitations] = useState<PlatformUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await invokeEdgeFunction<ListUsersResponse>("list-users", undefined, {
        strategy: "immediate",
        maxRetries: 2,
        retryDelay: 200,
        idempotent: true,
      });

      setUsers(response.users || []);
      setInvitations(response.invitations || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load users",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const inviteUser = useCallback(
    async (
      email: string,
      name: string,
      role: "DiscoRun" | "Manager" | "Viewer" | "PAR",
      companyId?: string | null
    ) => {
      try {
        const request: InviteUserRequest = { email, name, role, company_id: companyId ?? null };

        await invokeEdgeFunction<InviteUserResponse>("invite-user", request, {
          strategy: "linear",
          maxRetries: 2,
          retryDelay: 200,
          idempotent: true,
        });

        toast({
          title: "Invitation sent",
          description: `Invitation sent to ${email}`,
        });
        await fetchUsers();
        return { success: true };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to send invitation";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsers, toast]
  );

  const updateUserRole = useCallback(
    async (userId: string, role: "DiscoRun" | "Manager" | "Viewer" | "PAR") => {
      try {
        const request: UpdateUserRoleRequest = { userId, role };

        await invokeEdgeFunction<UpdateUserRoleResponse>("update-user-role", request, {
          strategy: "linear",
          maxRetries: 2,
          retryDelay: 200,
          idempotent: true,
        });

        toast({
          title: "Role updated",
          description: `User role updated to ${role}`,
        });
        await fetchUsers();
        return { success: true };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to update role";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsers, toast]
  );

  const updateUserCompany = useCallback(
    async (userId: string, companyId: string | null) => {
      try {
        const request: UpdateUserCompanyRequest = { userId, companyId };

        await invokeEdgeFunction<UpdateUserCompanyResponse>("update-user-company", request, {
          strategy: "linear",
          maxRetries: 2,
          retryDelay: 200,
          idempotent: true,
        });

        toast({
          title: "Company updated",
          description: companyId ? "User assigned to company" : "User unassigned from company",
        });
        await fetchUsers();
        return { success: true };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update company assignment";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsers, toast]
  );

  const removeUser = useCallback(
    async (userId: string) => {
      try {
        const request: RemoveUserRequest = { userId };

        await invokeEdgeFunction<RemoveUserResponse>("remove-user", request, {
          strategy: "linear",
          maxRetries: 2,
          retryDelay: 200,
          idempotent: true,
        });

        toast({
          title: "User removed",
          description: "User has been removed from the platform",
        });
        await fetchUsers();
        return { success: true };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to remove user";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsers, toast]
  );

  return {
    users,
    invitations,
    isLoading,
    fetchUsers,
    inviteUser,
    updateUserRole,
    updateUserCompany,
    removeUser,
  };
}
